import { useState, useEffect } from 'react';
import { captureSentryException } from '@/lib/sentry';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getAppUrl, PLAN_DISPLAY } from '@/lib/constants';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Check, X, Trash2 } from 'lucide-react';
import { useSubscription, getOverLimitStatus } from '@/hooks/useSubscription';
import { useFrozenItemCount } from '@/hooks/useFrozenItems';
import { DataExportCard } from '@/components/DataExportCard';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { Avatar } from '@/components/design/Avatar';
import { Layout } from '@/components/design/Layout';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { COOKIE_CONSENT_STORAGE_KEY } from '@/lib/constants';
import { initPostHog } from '@/lib/posthog';
import posthog from '@/lib/posthog';
import { Browser } from '@capacitor/browser';

/**
 *
 */
export default function Account() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { subscription, tier, maxItems, maxGroups, isPaid, isInGracePeriod, gracePeriodEndsAt, isOnTrial, trialDaysRemaining } = useSubscription();
  const { data: frozenItemCount = 0 } = useFrozenItemCount();
  const [searchParams] = useSearchParams();

  const [isEditingName, setIsEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [cookieConsent, setCookieConsent] = useState<string | null>(() =>
    localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY),
  );
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual' | null>(null);

  // Handle checkout success/cancel return from Stripe
  // [LEARN] Three queries are invalidated because a tier change affects subscription state, item
  // limits (frozen count), and usage counts. A single invalidation wouldn't refresh the full UI.
  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      toast.success('Subscription activated! Welcome to ShareShack Paid.');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['frozen-item-count'] });
      queryClient.invalidateQueries({ queryKey: ['my-items-count'] });
      posthog.capture('subscription_upgraded', { source: 'checkout_return' });
      navigate('/account', { replace: true });
    } else if (checkoutStatus === 'cancelled') {
      navigate('/account', { replace: true });
    }
  }, [searchParams, navigate, queryClient]);

  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { price_id: priceId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: async (data) => {
      if (data?.url) {
        await Browser.open({ url: data.url });
      }
    },
    onError: (error: Error) => {
      captureSentryException(error);
      toast.error('Failed to start checkout', { description: error.message });
    },
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: async (data) => {
      if (data?.url) {
        await Browser.open({ url: data.url });
      }
    },
    onError: (error: Error) => {
      captureSentryException(error);
      toast.error('Failed to open billing portal', { description: error.message });
    },
  });

  const deleteAccount = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('delete-account');
      // Two separate error paths: `error` covers network/invocation failures;
      // `data?.error` covers application-level errors returned in the response body
      // (e.g. "active loans exist").  Both must be checked because a successful HTTP
      // response does not guarantee a successful operation.
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast.success('Account deleted', {
        description: 'Your account and all data have been permanently removed.',
      });
    },
    onError: (error: Error) => {
      captureSentryException(error);
      toast.error('Failed to delete account', { description: error.message });
    },
  });

  const { data: itemCount = 0 } = useQuery({
    queryKey: ['my-items-count', user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('items')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user!.id)
        .eq('is_frozen', false);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const { data: groupCount = 0 } = useQuery({
    queryKey: ['my-groups-count', user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('group_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user!.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const {
    data: profile,
    isLoading,
    isError: profileError,
  } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, email_notifications')
        .eq('user_id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const updateName = useMutation({
    mutationFn: async (displayName: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName })
        .eq('user_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['group-members'] });
      toast.success('Display name updated!');
      setIsEditingName(false);
    },
    onError: (error: Error) => {
      captureSentryException(error);
      toast.error('Failed to update', { description: error.message });
    },
  });

  const updateEmailNotifications = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from('profiles')
        .update({ email_notifications: enabled })
        .eq('user_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error: Error) => {
      captureSentryException(error);
      toast.error('Failed to update notification settings', { description: error.message });
    },
  });

  const handleStartEdit = () => {
    setNewDisplayName(profile?.display_name || '');
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    const trimmed = newDisplayName.trim();
    if (!trimmed) return;
    updateName.mutate(trimmed);
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${getAppUrl()}/reset-password`,
    });
    if (error) {
      toast.error('Error', { description: error.message });
    } else {
      setResetEmailSent(true);
      toast.success('Check your email', { description: 'We sent you a password reset link.' });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const displayName = profile?.display_name || user?.email || '?';
  const userInitial = displayName[0].toUpperCase();

  /**
   * Returns the usage percentage capped at 100.
   * The cap guards against a user somehow exceeding their limit (e.g. a limit
   * was lowered after items were already created) so the progress bar never
   * overflows its container.  A `limit <= 0` guard prevents division by zero.
   * @param count
   * @param limit
   */
  const getUsagePercent = (count: number, limit: number) => {
    if (limit <= 0) return 0;
    return Math.min((count / limit) * 100, 100);
  };

  const SectionCard = ({ children }: { children: React.ReactNode }) => (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        overflow: 'hidden',
        marginBottom: 16,
        boxShadow: SHADOW.md,
      }}
    >
      {children}
    </div>
  );

  const SectionHeader = ({ label }: { label: string }) => (
    <div
      style={{
        padding: '9px 14px',
        background: T.surfaceSubtle,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: T.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontWeight: 700,
        }}
      >
        {label}
      </span>
    </div>
  );

  if (profileError) {
    return (
      <Layout title="Profile">
        <div style={{ textAlign: 'center', padding: '64px 24px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: T.danger, margin: '0 0 6px' }}>
            Failed to load profile
          </p>
          <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
            Please check your connection and try again.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Profile">
      {/* Profile section */}
      <SectionCard>
        <SectionHeader label="Profile" />
        <div style={{ padding: 16 }}>
          {/* Avatar + name row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Avatar name={userInitial} size={38} isCurrentUser />
            <div style={{ flex: 1, minWidth: 0 }}>
              {isEditingName ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Input
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder="Your name"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setIsEditingName(false);
                    }}
                    style={{ flex: 1, fontSize: 14 }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={updateName.isPending}
                    aria-label="Save name"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: T.success,
                      padding: 0,
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => setIsEditingName(false)}
                    aria-label="Cancel editing"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: T.textMuted,
                      padding: 0,
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>
                    {isLoading ? '…' : profile?.display_name || 'Not set'}
                  </span>
                  <button
                    onClick={handleStartEdit}
                    aria-label="Edit display name"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: T.textMuted,
                      padding: 0,
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Pencil size={14} />
                  </button>
                </div>
              )}
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                {user?.email || ''}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Subscription section */}
      <SectionCard>
        <SectionHeader label="Subscription" />
        <div style={{ padding: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <span
              style={{ fontSize: 15, fontWeight: 600, color: T.text, textTransform: 'capitalize' }}
            >
              {isOnTrial ? 'Free Trial' : `${tier} Plan`}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 9px',
                borderRadius: RADIUS.xl,
                background: isOnTrial ? T.accentSoft : isPaid ? T.successSoft : T.surfaceSubtle,
                color: isOnTrial ? T.accent : isPaid ? T.success : T.textMuted,
                border: `1px solid ${isOnTrial ? T.accentBorder : isPaid ? T.successBorder : T.border}`,
              }}
            >
              {isOnTrial ? 'Trial' : isPaid ? 'Paid' : 'Free'}
            </span>
          </div>

          {/* Items usage */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: T.textMuted }}>Items</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                {maxItems === -1 ? `${itemCount} — Unlimited` : `${itemCount} / ${maxItems}`}
              </span>
            </div>
            {maxItems !== -1 && (
              <>
                <div
                  style={{ height: 4, background: T.border, borderRadius: 4, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(getUsagePercent(itemCount, maxItems), 100)}%`,
                      background:
                        getUsagePercent(itemCount, maxItems) >= 100
                          ? T.accent
                          : getUsagePercent(itemCount, maxItems) >= 80
                            ? T.warning
                            : T.accent,
                      borderRadius: 4,
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                {getUsagePercent(itemCount, maxItems) >= 100 && !frozenItemCount ? (
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Item limit reached</p>
                ) : getUsagePercent(itemCount, maxItems) >= 80 && getUsagePercent(itemCount, maxItems) < 100 ? (
                  <p style={{ fontSize: 11, color: T.warning, marginTop: 4 }}>Approaching limit</p>
                ) : null}
              </>
            )}
            {/* Frozen items note (post-freeze) */}
            {frozenItemCount > 0 && (
              <p style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>
                You also have {frozenItemCount} frozen {frozenItemCount === 1 ? 'item' : 'items'} hidden from your groups.{' '}
                <span
                  style={{ color: T.accent, cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => navigate('/account#upgrade')}
                >
                  Upgrade to unlock →
                </span>
              </p>
            )}
          </div>

          {/* [LEARN] Grace period warning requires three conditions: in grace period AND not paid AND
              over the free-tier item cap. This prevents false warnings for users who had fewer items
              than the cap and don't need to worry about freezing. */}
          {isInGracePeriod && !isPaid && itemCount > maxItems && (
            <div
              style={{
                background: T.warningSoft,
                border: `1px solid ${T.warningBorder}`,
                borderRadius: RADIUS.md,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <p style={{ fontSize: 13, fontWeight: 600, color: T.warning, marginBottom: 4 }}>
                Your subscription has ended
              </p>
              <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.65 }}>
                {itemCount - maxItems} {itemCount - maxItems === 1 ? 'item' : 'items'} will be frozen on{' '}
                {gracePeriodEndsAt
                  ? new Date(gracePeriodEndsAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                    })
                  : 'soon'}
                . Upgrade to keep them all active.
              </p>
            </div>
          )}

          {/* Groups usage */}
          <div style={{ marginBottom: isPaid ? 0 : 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: T.textMuted }}>Groups</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                {maxGroups === -1
                  ? `${groupCount} — Unlimited`
                  : groupCount > maxGroups
                    ? `${groupCount} groups`
                    : `${maxGroups} group included`}
              </span>
            </div>
            {/* Over-limit groups warning (lapsed subscriber in multiple groups) */}
            {!isPaid && groupCount > maxGroups && (
              <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.65, marginTop: 2 }}>
                You belong to {groupCount} groups, but the free plan includes {maxGroups}.
                Your groups are safe — you can still access and use them all.
                To join or create new groups, upgrade your plan or leave a group.
              </p>
            )}
          </div>

          {isPaid && subscription?.current_period_end && (
            <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>
              Renews on {new Date(subscription.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}

          {isOnTrial && trialDaysRemaining !== null && (
            <p style={{ fontSize: 12, color: T.accent, fontWeight: 600, marginTop: 6 }}>
              {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
            </p>
          )}

          {isPaid && !isOnTrial && (
            <button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: RADIUS.md,
                background: T.surfaceSubtle,
                color: T.accent,
                border: `1px solid ${T.border}`,
                fontSize: 13,
                fontWeight: 600,
                cursor: portalMutation.isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                marginTop: 10,
              }}
            >
              {portalMutation.isPending ? 'Loading\u2026' : 'Manage Payment Method'}
            </button>
          )}

          {(!isPaid || isOnTrial) && (
            <div style={{ marginTop: 12 }}>
              {/* Plan cards */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {(['monthly', 'annual'] as const).map((plan) => {
                  const info = PLAN_DISPLAY[plan];
                  const isSelected = selectedPlan === plan;
                  return (
                    <button
                      key={plan}
                      onClick={() => setSelectedPlan(plan)}
                      style={{
                        flex: 1,
                        padding: '12px 8px',
                        borderRadius: RADIUS.md,
                        background: isSelected ? T.accentSoft : T.surfaceSubtle,
                        border: `2px solid ${isSelected ? T.accent : T.border}`,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'center',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.text }}>
                        {info.label}
                      </span>
                      <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: T.accent, marginTop: 2 }}>
                        {info.price}
                      </span>
                      {'saveBadge' in info && info.saveBadge && (
                        <span
                          style={{
                            display: 'inline-block',
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: RADIUS.xl,
                            background: T.successSoft,
                            color: T.success,
                            marginTop: 4,
                          }}
                        >
                          {info.saveBadge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Checkout CTA */}
              <button
                disabled={!selectedPlan || checkoutMutation.isPending}
                onClick={() => {
                  if (!selectedPlan) return;
                  checkoutMutation.mutate(PLAN_DISPLAY[selectedPlan].priceId);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: RADIUS.md,
                  background: selectedPlan ? T.accent : T.surfaceSubtle,
                  color: selectedPlan ? T.white : T.textMuted,
                  border: selectedPlan ? 'none' : `1px solid ${T.border}`,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: selectedPlan ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  boxShadow: selectedPlan ? SHADOW.btn.accent : 'none',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {checkoutMutation.isPending ? 'Loading\u2026' : 'Continue to shareshack.co.uk'}
              </button>

              {/* Apple external purchase disclosure */}
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: 8, lineHeight: 1.4 }}>
                You're about to leave the app. Any purchases you make will be managed by
                ShareShack, not Apple. Apple is not responsible for the privacy or security
                of transactions with this developer.
              </p>
            </div>
          )}

          <Link
            to="/refund-policy"
            style={{
              display: 'inline-block',
              fontSize: 12,
              color: T.accent,
              textDecoration: 'none',
              marginTop: 8,
            }}
          >
            Refund Policy
          </Link>
        </div>
      </SectionCard>

      {/* Security section */}
      <SectionCard>
        <SectionHeader label="Security" />
        <div style={{ padding: 16 }}>
          <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
            Click below to receive a password reset link at your email address.
          </p>
          <button
            onClick={handleResetPassword}
            disabled={resetEmailSent}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: resetEmailSent ? T.success : T.textSec,
              border: `1px solid ${resetEmailSent ? T.successBorder : T.border}`,
              fontSize: 13,
              fontWeight: 600,
              cursor: resetEmailSent ? 'default' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {resetEmailSent ? (
              <>
                <Check size={13} style={{ display: 'inline', marginRight: 4 }} />
                Reset Link Sent
              </>
            ) : (
              'Send Password Reset Link'
            )}
          </button>
        </div>
      </SectionCard>

      {/* Notifications */}
      <SectionCard>
        <SectionHeader label="Notifications" />
        <div style={{ padding: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 2px' }}>
                Email notifications
              </p>
              <p style={{ fontSize: 12, color: T.textMuted, margin: 0, lineHeight: 1.65 }}>
                Receive email alerts for loan requests, approvals, and overdue items
              </p>
            </div>
            <Switch
              checked={isLoading ? false : (profile?.email_notifications ?? true)}
              onCheckedChange={(checked) => updateEmailNotifications.mutate(checked)}
              disabled={updateEmailNotifications.isPending || isLoading}
              aria-label="Email notifications"
            />
          </div>
        </div>
      </SectionCard>

      {/* Data Export */}
      <SectionCard>
        <SectionHeader label="Data Export" />
        <DataExportCard />
      </SectionCard>

      {/* Cookie Preferences */}
      <SectionCard>
        <SectionHeader label="Cookie Preferences" />
        <div style={{ padding: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 13, color: T.textSec }}>Analytics cookies</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 9px',
                borderRadius: 9999,
                background: cookieConsent === 'accepted' ? T.successSoft : T.surfaceSubtle,
                color: cookieConsent === 'accepted' ? T.success : T.textMuted,
              }}
            >
              {cookieConsent === 'accepted' ? 'Accepted' : 'Declined'}
            </span>
          </div>
          <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.65 }}>
            We use PostHog to understand how ShareShack is used. No personal data is shared with
            third parties.
          </p>
          {cookieConsent === 'accepted' ? (
            <button
              onClick={() => {
                localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'declined');
                posthog.opt_out_capturing();
                setCookieConsent('declined');
                toast.success('Analytics cookies declined');
              }}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: RADIUS.md,
                background: T.surfaceSubtle,
                color: T.textSec,
                border: `1px solid ${T.border}`,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Withdraw consent
            </button>
          ) : (
            <button
              onClick={() => {
                localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'accepted');
                initPostHog();
                setCookieConsent('accepted');
                toast.success('Analytics cookies accepted');
              }}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: RADIUS.md,
                background: T.accent,
                color: T.white,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: SHADOW.btn.accent,
              }}
            >
              Allow analytics cookies
            </button>
          )}
        </div>
      </SectionCard>

      {/* Legal */}
      <SectionCard>
        <SectionHeader label="Legal" />
        <div style={{ padding: 0 }}>
          {[
            { label: 'Privacy Policy', to: '/privacy' },
            { label: 'Terms & Conditions', to: '/terms' },
            { label: 'Refund Policy', to: '/refund-policy' },
          ].map((item, i, arr) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '13px 16px',
                fontSize: 13,
                color: T.text,
                textDecoration: 'none',
                borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
                cursor: 'pointer',
              }}
            >
              <span>{item.label}</span>
              <span style={{ color: T.textMuted, fontSize: 16 }}>›</span>
            </Link>
          ))}
        </div>
      </SectionCard>

      {/* Sign Out */}
      <button
        onClick={handleSignOut}
        style={{
          width: '100%',
          padding: '13px',
          borderRadius: RADIUS.md,
          background: T.accent,
          color: T.white,
          border: 'none',
          fontSize: 15,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          marginBottom: 16,
          boxShadow: SHADOW.btn.accent,
        }}
      >
        Sign Out
      </button>

      {/* Danger Zone */}
      <div
        style={{
          border: `1px solid ${T.dangerBorder}`,
          borderRadius: RADIUS.lg,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '9px 14px',
            background: T.dangerSoft,
            borderBottom: `1px solid ${T.dangerBorder}`,
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: T.danger,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 700,
            }}
          >
            Danger Zone
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <p style={{ fontSize: 14, color: T.textSec, marginBottom: 14, lineHeight: 1.65 }}>
            This action is <strong>irreversible</strong>. All your items, group memberships, loan
            history, notifications, and profile data will be permanently removed. You must return
            all active loans before deleting your account.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 16px',
                  borderRadius: RADIUS.md,
                  background: T.danger,
                  color: T.white,
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: SHADOW.btn.danger,
                }}
              >
                <Trash2 size={14} />
                Delete My Account
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-3">
                  <span className="block">
                    This will permanently delete your account and all data including:
                  </span>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Your profile and display name</li>
                    <li>All items you own</li>
                    <li>All group memberships</li>
                    <li>Your loan history</li>
                    <li>All notifications</li>
                    <li>Your subscription</li>
                  </ul>
                  <span className="block font-medium">
                    Type <strong>DELETE</strong> below to confirm:
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input
                placeholder="Type DELETE to confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="mt-2"
              />
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={deleteConfirmText !== 'DELETE' || deleteAccount.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    deleteAccount.mutate();
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteAccount.isPending ? 'Deleting…' : 'Permanently Delete Account'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Layout>
  );
}
